import { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { TextStyle, FontFamily, FontSize } from "@tiptap/extension-text-style";
import {
  TextB, TextItalic, TextUnderline, ListBullets, ListNumbers,
} from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BookmarksSimple, Plus, Trash } from "@phosphor-icons/react";

const FONT_FAMILIES = [
  { label: "Default", value: "" },
  { label: "Sans", value: "Arial, Helvetica, sans-serif" },
  { label: "Serif", value: "Georgia, 'Times New Roman', serif" },
  { label: "Mono", value: "'Courier New', monospace" },
];
const FONT_SIZES = ["", "12", "14", "16", "18", "24", "32"];

function ToolbarBtn({ active, onClick, children, testId, title }) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      data-testid={testId}
      className={`h-8 w-8 flex items-center justify-center rounded-md transition-colors ${
        active ? "bg-pf-primary text-white" : "text-pf-text-secondary hover:bg-pf-bg"
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Rich-text field backed by TipTap. Stores HTML in `value`, calls `onChange(html)`.
 * Optional `snippetKind` ("recommendations" | "clinical-examinations") enables a
 * per-clinic saved-snippets picker mirroring the old Saved Recommendations UX.
 */
export default function RichTextField({
  value = "",
  onChange,
  onBlur,
  testId = "rich",
  snippetKind = null,
  placeholder = "",
}) {
  const lastEmitted = useRef(value);

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextStyle,
      FontFamily,
      FontSize,
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      lastEmitted.current = html;
      onChange && onChange(html);
    },
    onBlur: () => onBlur && onBlur(),
    editorProps: {
      attributes: {
        class: "pf-richtext-content",
        "data-testid": `${testId}-input`,
      },
    },
  });

  // Keep editor in sync when the external value changes (load, snippet insert).
  useEffect(() => {
    if (!editor) return;
    if ((value || "") !== lastEmitted.current) {
      editor.commands.setContent(value || "", false);
      lastEmitted.current = value || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div className="pf-richtext" data-testid={testId}>
      {snippetKind && (
        <SnippetBar kind={snippetKind} testId={testId} editor={editor} />
      )}
      <div className="flex flex-wrap items-center gap-1 border border-pf-border rounded-t-lg px-2 py-1.5 bg-pf-bg/40">
        <ToolbarBtn
          testId={`${testId}-bold`}
          title="Bold"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <TextB size={16} weight="bold" />
        </ToolbarBtn>
        <ToolbarBtn
          testId={`${testId}-italic`}
          title="Italic"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <TextItalic size={16} />
        </ToolbarBtn>
        <ToolbarBtn
          testId={`${testId}-underline`}
          title="Underline"
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <TextUnderline size={16} />
        </ToolbarBtn>
        <div className="w-px h-5 bg-pf-border mx-1" />
        <ToolbarBtn
          testId={`${testId}-bullet`}
          title="Bullet list"
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <ListBullets size={16} />
        </ToolbarBtn>
        <ToolbarBtn
          testId={`${testId}-ordered`}
          title="Numbered list"
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListNumbers size={16} />
        </ToolbarBtn>
        <div className="w-px h-5 bg-pf-border mx-1" />
        <select
          data-testid={`${testId}-fontfamily`}
          className="text-xs border border-pf-border rounded-md px-1.5 py-1 bg-white"
          onChange={(e) => {
            const v = e.target.value;
            if (v) editor.chain().focus().setFontFamily(v).run();
            else editor.chain().focus().unsetFontFamily().run();
          }}
          value=""
        >
          {FONT_FAMILIES.map((f) => (
            <option key={f.label} value={f.value}>{f.label}</option>
          ))}
        </select>
        <select
          data-testid={`${testId}-fontsize`}
          className="text-xs border border-pf-border rounded-md px-1.5 py-1 bg-white"
          onChange={(e) => {
            const v = e.target.value;
            if (v) editor.chain().focus().setFontSize(`${v}px`).run();
            else editor.chain().focus().unsetFontSize().run();
          }}
          value=""
        >
          <option value="">Size</option>
          {FONT_SIZES.filter(Boolean).map((s) => (
            <option key={s} value={s}>{s}px</option>
          ))}
        </select>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

function SnippetBar({ kind, testId, editor }) {
  const { t } = useLang();
  const [snippets, setSnippets] = useState([]);
  const [open, setOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const { data } = await api.get(`/${kind}`);
      setSnippets(data);
    } catch (e) {
      /* silent — field still works */
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  function insert(s) {
    editor.chain().focus().insertContent(s.body || "").run();
    setOpen(false);
  }

  async function saveNew() {
    const html = editor.getHTML();
    const text = editor.getText().trim();
    if (!newTitle.trim()) { toast.error(t("required")); return; }
    if (!text) { toast.error(t("recNothingToSave") || "Nothing to save"); return; }
    setBusy(true);
    try {
      await api.post(`/${kind}`, { title: newTitle.trim(), body: html });
      toast.success(t("saved"));
      setNewTitle(""); setSaveOpen(false); load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  }

  async function del(id) {
    if (!window.confirm(t("delete") + "?")) return;
    try { await api.delete(`/${kind}/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="flex flex-wrap items-center gap-2 mb-2">
      <button type="button" onClick={() => setOpen((s) => !s)}
        className="pf-btn-ghost !py-1 !px-2 text-xs" data-testid={`${testId}-open-saved`}>
        <BookmarksSimple size={13} /> {t("recSaved")} ({snippets.length})
      </button>
      <button type="button" onClick={() => setSaveOpen(true)}
        className="pf-btn-ghost !py-1 !px-2 text-xs" data-testid={`${testId}-save-new`}>
        <Plus size={13} /> {t("recSaveNew")}
      </button>
      <button type="button" onClick={() => setManageOpen(true)}
        className="pf-btn-ghost !py-1 !px-2 text-xs text-pf-text-secondary" data-testid={`${testId}-manage`}>
        {t("recManage")}
      </button>

      {open && snippets.length > 0 && (
        <div className="w-full pf-card p-1 max-h-64 overflow-y-auto" data-testid={`${testId}-suggest`}>
          {snippets.map((s) => (
            <button key={s.id} type="button" onClick={() => insert(s)}
              className="w-full text-left rounded-md px-3 py-2 hover:bg-pf-primary-light transition-colors"
              data-testid={`${testId}-snippet-${s.id}`}>
              <div className="text-sm font-semibold text-pf-text">{s.title}</div>
              <div className="text-xs text-pf-text-secondary line-clamp-2"
                   dangerouslySetInnerHTML={{ __html: s.body }} />
            </button>
          ))}
        </div>
      )}

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">{t("recSaveNewTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="pf-label mb-1 block">{t("recTitle")}</label>
              <input data-testid={`${testId}-new-title`} className="pf-input"
                value={newTitle} onChange={(e) => setNewTitle(e.target.value)} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setSaveOpen(false)} className="pf-btn-ghost">{t("cancel")}</button>
              <button onClick={saveNew} disabled={busy} className="pf-btn-primary"
                data-testid={`${testId}-confirm-save`}>{t("save")}</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{t("recManageTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {snippets.length === 0 ? (
              <div className="text-sm text-pf-text-secondary text-center py-6">{t("noResults")}</div>
            ) : snippets.map((s) => (
              <div key={s.id} className="border border-pf-border rounded-lg p-3 flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-pf-text">{s.title}</div>
                  <div className="text-xs text-pf-text-secondary line-clamp-4 mt-1"
                       dangerouslySetInnerHTML={{ __html: s.body }} />
                </div>
                <button onClick={() => del(s.id)} className="pf-btn-ghost !py-1 !px-2 text-pf-danger"
                  data-testid={`${testId}-delete-${s.id}`}>
                  <Trash size={13} />
                </button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
